const ALLOWED_DOMAINS = {
  PRODUCTION: 'hivi.com.br',
  STAGING: 'staging.hivi.com.br',
} as const;

export type Environment = 'STAGING' | 'PRODUCTION';

function isAllowedDomain(hostname: string): boolean {
  const normalized = hostname.toLowerCase().trim();

  return (
    normalized === ALLOWED_DOMAINS.PRODUCTION ||
    normalized === ALLOWED_DOMAINS.STAGING ||
    normalized.endsWith(`.${ALLOWED_DOMAINS.PRODUCTION}`) ||
    normalized.endsWith(`.${ALLOWED_DOMAINS.STAGING}`)
  );
}

export function detectEnvironment(hostname: string): Environment | null {
  const normalized = hostname.toLowerCase().trim();

  if (
    normalized === ALLOWED_DOMAINS.STAGING ||
    normalized.endsWith(`.${ALLOWED_DOMAINS.STAGING}`)
  ) {
    return 'STAGING';
  }

  if (
    (normalized === ALLOWED_DOMAINS.PRODUCTION ||
      normalized.endsWith(`.${ALLOWED_DOMAINS.PRODUCTION}`)) &&
    !normalized.includes('staging')
  ) {
    return 'PRODUCTION';
  }

  return null;
}

function extractSubdomain(hostname: string, environment: Environment | null): string | null {
  if (!environment) return null;

  const normalized = hostname.toLowerCase().trim();
  const baseDomain = ALLOWED_DOMAINS[environment];

  if (normalized === baseDomain) {
    return null;
  }

  if (normalized.endsWith(`.${baseDomain}`)) {
    const subdomain = normalized.slice(0, -(baseDomain.length + 1));
    if (subdomain && !subdomain.includes('.') && subdomain !== 'www') {
      return subdomain;
    }
  }

  return null;
}

function isValidSlug(slug: string | null): boolean {
  if (!slug) return false;
  if (slug.length > 63) return false;
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug);
}

/** Slug do estabelecimento a partir do hostname. Dev: `tenant.localhost`. */
export function getEstablishmentSlugFromUrl(): string | null {
  const hostname = window.location.hostname.toLowerCase().trim();

  if (hostname.includes('.localhost')) {
    const slug = hostname.split('.')[0];
    return slug || null;
  }

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  if (!isAllowedDomain(hostname)) {
    console.warn(`[slugHelper] Domínio não permitido: ${hostname}`);
    return null;
  }

  const environment = detectEnvironment(hostname);
  return extractSubdomain(hostname, environment);
}

/** true quando há subdomínio de tenant (não é só home institucional). */
export function shouldMakeApiCalls(): boolean {
  const hostname = window.location.hostname.toLowerCase();

  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  if (!isAllowedDomain(hostname)) {
    return false;
  }

  const environment = detectEnvironment(hostname);
  const subdomain = extractSubdomain(hostname, environment);
  return subdomain !== null;
}

/** Mesma condição que o ramo tenant em AppRoutes. */
export function isEstablishmentRouteContext(): boolean {
  if (typeof window === 'undefined') return false;

  const establishmentSlug = getEstablishmentSlugFromUrl();
  const hasValidSlug = !!establishmentSlug && isValidSlug(establishmentSlug);
  return hasValidSlug && shouldMakeApiCalls();
}
